<template>
  <div class="mb-2">
    <div class="b-form__container_type_flex">
      <checkbox
        class="b-checkbox"
        :disabled="isDisabledExtension"
        :id="`extension-${currentAssetComponent.assetId}-${currentAssetComponentIndex}`"
        v-model="isExtension"
      >{{$t('CONTENT.assets.extension')}}</checkbox>
      <dynamic-icon class="ml-1" v-tooltip="$t('CONTENT.hints.extensionHint')" icon="question"></dynamic-icon>
    </div>
    <transition name="fade">
      <div class="my-4 ml-6" v-if="isExtension">
        <div>
          <checkbox
            class="b-checkbox"
            :id="`is-extension-${currentAssetComponent.assetId}-${currentAssetComponentIndex}`"
            v-model="extensionIsSure"
          >{{$t('CONTENT.assets.options_is_extended')}}</checkbox>
        </div>
        <div class="row mt-4 px-0">
          <div class="col-2">
            <div class="b-form__container" data-cy="extension-month-count">
              <label>{{$t('CONTENT.assets.option_month_count')}}:</label>
              <numeric
                class="b-numeric"
                controls
                :min="0"
                v-model="extensionMonthCount"
                :disabled="!extensionIsSure || isReadOnlyField"
                @keypress="changeNumericValue($event)"
                @change="onChangeExtensionMonthCount"
              ></numeric>
            </div>
          </div>
          <div class="col-2">
            <div class="b-form__container">
              <label>{{$t('CONTENT.date')}}:</label>
              <div class="is-manually">
                <div v-if="isManuallyExtensionDate" class="is-manually-mark" :title="`${$t('CONTENT.isManuallyData')}`"></div>
                <datepicker
                  class="b-datepicker"
                  v-model="extensionDate"
                  :disabled="!extensionIsSure || isReadOnlyField"
                  :formatter="datepickerFormat"
                  :disabled-date="startAssetComponentDate"
                ></datepicker>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import dateConverter from "../../../../../utils/converters/date.converter";
import DateWithoutTimeAPI from "../../../../../api/DateWithoutTimeAPI";
import ExtensionOption from "../../../../../models/contract/ExtensionOption";
import TerminationOption from "../../../../../models/contract/TerminationOption";
import changeNumericValue from "../../../../../mixins/changeNumericValue";
import datepickerFormat from '@/mixins/datepickerFormat.mixin';

export default {
  props: {
    currentAssetComponent: {
      type: Object
    },
    currentAssetComponentIndex: {
      type: Number
    }
  },
  data() {
    return {
      extensionMonthCount: null,
      isManuallyExtensionDate: false,
      isReadOnlyField: false
    };
  },
  mixins: [changeNumericValue, datepickerFormat],
  computed: {
    isDisabledExtension() {
      return !!this.currentAssetComponent.isPermanent;
    },
    isExtension: {
      get() {
        return this.currentAssetComponent.extension !== null;
      },
      set(value) {
        if (value) {
          this.$store.commit("assetComponentsPlugin/addExtensionOption", {
            assetId: this.currentAssetComponent.assetId,
            assetComponent: this.currentAssetComponent,
            data: ExtensionOption.createNew()
          });
          this.extensionMonthCount = null;
          this.isManuallyExtensionDate = false;
        } else {
          this.$store.commit("assetComponentsPlugin/addExtensionOption", {
            assetId: this.currentAssetComponent.assetId,
            assetComponent: this.currentAssetComponent,
            data: null
          });

          this.extensionMonthCount = null;
          this.isManuallyExtensionDate = false;
        }
        this.changeExtension();
      }
    },
    extensionIsSure: {
      get() {
        if (this.currentAssetComponent.extension === null) {
          return false;
        } else {
          return this.currentAssetComponent.extension.isSure;
        }
      },
      set(value) {
        if (value) {
          this.$store.commit("assetComponentsPlugin/addExtensionOption", {
            assetId: this.currentAssetComponent.assetId,
            assetComponent: this.currentAssetComponent,
            data: ExtensionOption.createNew()
          });
          this.extensionMonthCount = null;
          this.isManuallyExtensionDate = false;
        }

        this.$store.commit("assetComponentsPlugin/updateExtensionOption", {
          assetId: this.currentAssetComponent.assetId,
          name: "isSure",
          assetComponent: this.currentAssetComponent,
          value: value
        });

        if (
          value &&
          this.currentAssetComponent.termination !== null &&
            this.currentAssetComponent.termination.isSure
        ) {
          this.$store.commit("assetComponentsPlugin/addTerminationOption", {
            assetId: this.currentAssetComponent.assetId,
            assetComponent: this.currentAssetComponent,
            data: TerminationOption.createNew()
          });
        }
        this.changeExtension();
      }
    },
    extensionDate: {
      get() {
        if (
          this.currentAssetComponent.extension == null ||
          this.currentAssetComponent.extension.date == null
        ) {
          return null;
        } else {
          return dateConverter.convertToDate(
            this.currentAssetComponent.extension.date
          );
        }
      },
      set(newVal) {
        if (newVal !== null) {
          let extensionDate = dateConverter.convertToString(newVal);
          this.updateExtensionDate(extensionDate);
          this.isManuallyExtensionDate = true;
          this.extensionMonthCount = null;
        } else {
          this.updateExtensionDate(null);
          this.extensionMonthCount = null;
          this.isManuallyExtensionDate = false;
        }
      }
    }
  },
  watch: {
    "currentAssetComponent.isPermanent"(newVal) {
      if (newVal) {
        this.$store.commit("assetComponentsPlugin/addExtensionOption", {
          assetId: this.currentAssetComponent.assetId,
          assetComponent: this.currentAssetComponent,
          data: null
        });
        this.extensionMonthCount = null;
        this.isManuallyExtensionDate = false;
        this.changeExtension();
      }
    },
    "currentAssetComponent.endDate"(val) {
      if (val !== null && this.extensionMonthCount !== null) {
        this.onChangeExtensionMonthCount(this.extensionMonthCount);
      } else if (val === null && this.currentAssetComponent.extension !== null) {
        this.updateExtensionDate(null);
        this.extensionMonthCount = null;
      }
    },
    extensionIsSure(val) {
      if (!val && this.isExtension) {
        this.extensionMonthCount = null;
        this.isManuallyExtensionDate = false;
        this.$store.commit("assetComponentsPlugin/addExtensionOption", {
          assetId: this.currentAssetComponent.assetId,
          assetComponent: this.currentAssetComponent,
          data: ExtensionOption.createNew()
        });
        this.changeExtension();
      }
    }
  },
  methods: {
    startAssetComponentDate(date) {
      if (this.currentAssetComponent.startDate !== null) {
        return (
          date <
          dateConverter.convertToDate(this.currentAssetComponent.startDate)
        );
      }
    },
    async onChangeExtensionMonthCount(newVal) {
      if (newVal !== 0) {
        this.isManuallyExtensionDate = false;
        try {
          this.error = null;
          this.isReadOnlyField = true;
          let currentAssetComponentEndDate = this.currentAssetComponent.endDate;
          if (
            currentAssetComponentEndDate !== null &&
            currentAssetComponentEndDate !== ""
          ) {
            const convertedCurrentAssetComponentEndDate = encodeURIComponent(
              currentAssetComponentEndDate
            );
            let newDate = await new DateWithoutTimeAPI().addMonths(
              convertedCurrentAssetComponentEndDate,
              newVal
            );
            this.updateExtensionDate(newDate);
          }
          this.isReadOnlyField = false;
        } catch (e) {
          this.isReadOnlyField = false;
          this.error = e;
        }
      }
    },
    updateExtensionDate(value) {
      this.$store.commit("assetComponentsPlugin/updateExtensionOption", {
        assetId: this.currentAssetComponent.assetId,
        name: "date",
        assetComponent: this.currentAssetComponent,
        value: value
      });
      this.changeExtension();
    },
    changeExtension() {
      this.$emit("changeExtension");
    }
  }
};
</script>

